import { Box } from "@vp/swan";
import React, { useEffect, useMemo, useState } from "react";
import { SHOW_ELEV8_PRICES } from "../../constants/featureFlags";
import { useFeatureToggle } from "../../hooks/useFeatureToggle";
import usePricingContextInitialization from "../../hooks/usePricingContextInitialization";
import { useProductStore } from "../../store/productPriceDetails.store";
import { PageId } from "../../types/pageId";
import { PricePresentationType, PriceProperties } from "../../types/prices";
import { getPresentationType } from "../../util/getPresentationType";
import { isRangePriceDisplayGroup } from "../../util/price-calculator-helper";
import { getPriceQueryKey } from "../../util/price-extraction";
import { PriceDisplayProps } from "../PriceDisplay";
import PriceLoading from "../PriceLoading";
import PricePresentation, { PricePresentationProps } from "../PricePresentation";
import { SurchargeMessage } from "../SurchargeMessage/SurchargeMessage";

// 1. Accept details like mpvId, selections (optionally), quantity (optionally) or quantityEnum (optionally),
// 2. Fetch price details from store
// 3. Pass the price details to this component

export type PriceExtractionWrapperProps = PriceDisplayProps;

const PriceExtractionWrapper = ({
  mpvId,
  productKey,
  version,
  selections,
  quantity,
  cacheableQuantities,
  requestedDisplayStrategy,
  hideSetupFee,
  hidePerUnitPrice,
  hideVolumeDiscount,
  hideMinimumOrderableQuantity,
}: PriceExtractionWrapperProps) => {
  const { isFeatureFlagEnabled } = useFeatureToggle();
  const showElev8PricePresentation = isFeatureFlagEnabled(SHOW_ELEV8_PRICES);

  const addPriceQuery = useProductStore((state) => state.addPriceQuery);
  const basePriceExtractionConfig = useProductStore((state) => state.basePriceExtractionConfig);
  const vatIncl = useProductStore((state) => state.vatIncl) || false;

  const { locale = "", tenant = "", developmentMode = false } = basePriceExtractionConfig || {};
  const [pageId, setPageId] = useState<PageId | undefined>();

  // States to cache last valid query key and quantity
  const [lastValidPriceQueryKey, setLastValidPriceQueryKey] = useState<string | undefined>(undefined);
  const [lastValidQuantity, setLastValidQuantity] = useState<number | undefined>(quantity);

  useEffect(() => {
    if (!pageId && basePriceExtractionConfig?.pageId) {
      setPageId(basePriceExtractionConfig?.pageId);
    }
  }, [basePriceExtractionConfig, pageId]);

  const pricingContextInitialized = usePricingContextInitialization(
    tenant,
    locale,
    developmentMode,
  );

  // Generating a unique key for the current price query using provided parameters
  const priceQueryKey =
    getPriceQueryKey({
      mpvId,
      selections,
      quantity,
      cacheableQuantities,
      productKey,
      version,
    }) || "";

  // Retrieving the product price details from the store based on the generated price query key
  const currentProductPriceDetails = useProductStore(
    (state) => state.productPriceDetails?.[priceQueryKey],
  );
  const isError = useProductStore(
    (state) => state.isError,
  ) || currentProductPriceDetails?.isError;
  const lastValidProductPriceDetails = useProductStore(
    (state) => (lastValidPriceQueryKey ? state.productPriceDetails?.[lastValidPriceQueryKey] : undefined),
  );

  // Adding a price query to the store when any of the dependencies change
  useEffect(() => {
    if (!pricingContextInitialized || !basePriceExtractionConfig || !pageId) return;
    addPriceQuery({
      mpvId,
      productKey,
      version,
      selections,
      quantity,
      cacheableQuantities,
      requestedDisplayStrategy,
    }, pageId);
  }, [
    addPriceQuery,
    mpvId,
    quantity,
    cacheableQuantities,
    selections,
    requestedDisplayStrategy,
    productKey,
    version,
    vatIncl,
    pricingContextInitialized,
    pageId,
  ]);

  // Update cached valid query key and quantity when new data arrives
  useEffect(() => {
    if (currentProductPriceDetails?.prices?.estimatedPrices) {
      setLastValidPriceQueryKey(priceQueryKey);
      setLastValidQuantity(quantity);
    }
  }, [currentProductPriceDetails, priceQueryKey, quantity]);

  // Decide which productPriceDetails to use: current if available, else cached fallback
  const priceDetailsToUse = currentProductPriceDetails?.prices?.estimatedPrices
    ? currentProductPriceDetails
    : lastValidProductPriceDetails;

  const hasProductPriceDetails = priceDetailsToUse?.prices && priceDetailsToUse?.prices.estimatedPrices;

  const estimatedPrices = priceDetailsToUse?.prices?.estimatedPrices ?? {};

  // get different quantities
  const { minimumOrderableQuantity, maximumOrderableQuantity, defaultQuantity } = priceDetailsToUse?.quantityMetaData ?? {};
  const fallbackQuantity = defaultQuantity ?? minimumOrderableQuantity ?? Number(Object.keys(estimatedPrices)[0]);
  const isRangeStrategy = priceDetailsToUse?.displayStrategy === PricePresentationType.UnitRangePrice;

  const getEvaluatedQuantity = (): number => {
    if (isRangeStrategy) return minimumOrderableQuantity ?? fallbackQuantity;
    if (currentProductPriceDetails?.prices?.estimatedPrices && quantity && quantity > 0) return quantity;
    if (lastValidQuantity && lastValidQuantity > 0) return lastValidQuantity;
    return fallbackQuantity;
  };

  const evaluatedQuantity: number = getEvaluatedQuantity();
  const isValidQuantity = typeof evaluatedQuantity === "number" && !isNaN(evaluatedQuantity);

  const resolvedPrices = useMemo(() => {
    if (!priceDetailsToUse || !isValidQuantity) return null;
    const displayStrategy = priceDetailsToUse?.displayStrategy;
    const isUnitRangePrice = displayStrategy === PricePresentationType.UnitRangePrice;

    if (isUnitRangePrice && minimumOrderableQuantity && maximumOrderableQuantity) {
      const priceAtMin = estimatedPrices[minimumOrderableQuantity];
      const priceAtMax = estimatedPrices[maximumOrderableQuantity];
      return {
        highestListPrice: priceAtMin?.unitListPrice,
        highestDiscountPrice: priceAtMin?.unitDiscountedPrice,
        lowestListPrice: priceAtMax?.unitListPrice,
        lowestDiscountPrice: priceAtMax?.unitDiscountedPrice,
        setupFeeListPrice: priceAtMin?.subtotalAmounts?.setupFee?.listPrice ?? { taxed: 0, untaxed: 0 },
        setupFeeDiscountedPrice: priceAtMin?.subtotalAmounts?.setupFee?.discountedPrice ?? { taxed: 0, untaxed: 0 },
      };
    }

    let currentPriceInfo = estimatedPrices[evaluatedQuantity];
    if (displayStrategy === PricePresentationType.PriceWithQtyAndUnit && minimumOrderableQuantity) {
      const priceAtMin = estimatedPrices[minimumOrderableQuantity];
      currentPriceInfo = {
        ...currentPriceInfo,
        unitListPriceForMoq: priceAtMin?.unitListPrice ?? currentPriceInfo?.unitListPrice,
        setupFeeListPrice: priceAtMin?.subtotalAmounts?.setupFee?.listPrice ?? { taxed: 0, untaxed: 0 },
        setupFeeDiscountedPrice: priceAtMin?.subtotalAmounts?.setupFee?.discountedPrice ?? { taxed: 0, untaxed: 0 },
      };
    }

    return currentPriceInfo;
  }, [
    priceDetailsToUse?.displayStrategy,
    estimatedPrices,
    evaluatedQuantity,
    minimumOrderableQuantity,
    maximumOrderableQuantity,
  ]);

  // Return empty fragment ONLY if:
  // - API Fails
  // - No data came back
  // - missing input quantity price data
  const isMissingResolvedPrices = priceDetailsToUse?.isError === false && !resolvedPrices;
  if (isError || priceDetailsToUse?.isError || isMissingResolvedPrices) {
    return <></>;
  }

  if (pricingContextInitialized && basePriceExtractionConfig && hasProductPriceDetails && pageId) {
    const priceProperties: PriceProperties & { quantity: number } = {
      minimumFractionDigits: priceDetailsToUse.prices.fractionDigits,
      locale: locale,
      currency: priceDetailsToUse.prices.currency,
      vatInc: Boolean(vatIncl),
      quantity: evaluatedQuantity,
      hideSetupFee: hideSetupFee,
      hidePerUnitPrice: hidePerUnitPrice,
      hideVolumeDiscount: hideVolumeDiscount,
      hideMinimumOrderableQuantity: hideMinimumOrderableQuantity,
    };

    const isRangePriceProduct = isRangePriceDisplayGroup(priceDetailsToUse?.priceDisplayGroup);
    if (priceDetailsToUse?.displayStrategy === PricePresentationType.PriceWithQtyAndUnit) {
      const taxed = estimatedPrices[evaluatedQuantity]?.subtotalAmounts?.setupFee?.listPrice?.taxed;
      priceProperties.isSetupFeeIncluded = !!taxed && taxed !== 0;
    }

    // TODO after elev8 test: to be updated to priceDetailsToUse.displayStrategy once we remove feature gate for elev8
    const presentationType = getPresentationType(priceDetailsToUse?.priceDisplayGroup, priceDetailsToUse.displayStrategy, showElev8PricePresentation, pageId);
    return (
      <Box aria-live="polite" aria-atomic="true" data-testid="price-extraction-wrapper-test-id">
        <PricePresentation
          {...({
            presentationType,
            prices: resolvedPrices,
            priceProperties,
            pageId,
            priceDisplayGroup: priceDetailsToUse?.priceDisplayGroup,
          } as PricePresentationProps)}
        />
        {isRangePriceProduct && productKey && version && <SurchargeMessage locale={locale} productKey={productKey} version={version} pageId={pageId} />}
      </Box>
    );
  }

  return (
    <PriceLoading />
  );
};

export { PriceExtractionWrapper };
